import React, { useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState, ChartControls } from '../ui';
import { useCustomerTrends, SectionProps } from '../../hooks/useCustomerAnalyticsSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { formatNumber } from '../../utils/formatters';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface CustomerTrendsWithBalanceChartProps extends SectionProps {
  branchId: number;
  months?: number;
  title?: string;
  description?: string;
}

const CustomerTrendsWithBalanceChart: React.FC<CustomerTrendsWithBalanceChartProps> = ({
  branchId,
  months = 12,
  title = 'تحلیل حفظ مشتریان',
  description,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: trendsData, isLoading, isError, error, refetch } = useCustomerTrends(branchId, months);

  const getJalaliNow = React.useCallback(() => {
    const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }).formatToParts(new Date());

    const yearPart = parts.find(p => p.type === 'year')?.value || '0';
    const monthPart = parts.find(p => p.type === 'month')?.value || '0';
    const dayPart = parts.find(p => p.type === 'day')?.value || '0';

    const toEnglishDigits = (value: string): string =>
      value.replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));

    return {
      year: parseInt(toEnglishDigits(yearPart), 10),
      month: parseInt(toEnglishDigits(monthPart), 10),
      day: parseInt(toEnglishDigits(dayPart), 10)
    };
  }, []);

  const isJalaliLeapYear = React.useCallback((jy: number): boolean => {
    const breaks = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178];
    let jump = 0;
    let n = 0;
    let i = 1;
    let leapJ = -14;
    let jp = breaks[0];
    let jm = 0;

    for (; i < breaks.length; i++) {
      jm = breaks[i];
      jump = jm - jp;
      if (jy < jm) break;
      leapJ = leapJ + Math.floor(jump / 33) * 8 + Math.floor((jump % 33) / 4);
      jp = jm;
    }

    n = jy - jp;
    leapJ = leapJ + Math.floor(n / 33) * 8 + Math.floor(((n % 33) + 3) / 4);
    if (jump % 33 === 4 && jump - n === 4) {
      leapJ += 1;
    }

    return ((leapJ + 1) % 33 - 1) % 4 === 0;
  }, []);

  const getJalaliMonthDays = React.useCallback((year: number, month: number): number => {
    if (month <= 6) return 31;
    if (month <= 11) return 30;
    return isJalaliLeapYear(year) ? 30 : 29;
  }, [isJalaliLeapYear]);

  const getMonthDaysForLabel = React.useCallback((monthLabel: string): number => {
    const indexMap: Record<string, number> = {
      فروردین: 1,
      اردیبهشت: 2,
      خرداد: 3,
      تیر: 4,
      مرداد: 5,
      شهریور: 6,
      مهر: 7,
      آبان: 8,
      آذر: 9,
      دی: 10,
      بهمن: 11,
      اسفند: 12
    };

    const monthNum = indexMap[monthLabel];
    if (!monthNum) {
      return 30;
    }

    const jalaliNow = getJalaliNow();
    return getJalaliMonthDays(jalaliNow.year, monthNum);
  }, [getJalaliMonthDays, getJalaliNow]);

  const getAdjustedLastMonthValue = React.useCallback((value: number, monthLabel: string): number => {
    if (!monthLabel) return value;
    const currentMonth = getJalaliNow();
    const isCurrentMonth = trendsData && trendsData.length > 0
      ? monthLabel === trendsData[trendsData.length - 1]?.month
      : false;
    if (!isCurrentMonth) return value;

    const currentDay = Math.max(1, Math.min(currentMonth.day - 1, getMonthDaysForLabel(monthLabel)));
    const fullMonthDays = getMonthDaysForLabel(monthLabel);
    return (value / currentDay) * fullMonthDays;
  }, [getJalaliNow, getMonthDaysForLabel, trendsData]);

  const componentId = 'customer-trends-balance-chart';
  const componentTitle = title;

  const lastTrend = trendsData && trendsData.length > 0
    ? trendsData[trendsData.length - 1]
    : undefined;
  const lastTrendAdjustedNew = lastTrend ? getAdjustedLastMonthValue(lastTrend.newCustomers, lastTrend.month) : 0;
  const lastTrendAdjustedOld = lastTrend ? getAdjustedLastMonthValue(lastTrend.oldCustomers, lastTrend.month) : 0;
  const roundDisplayValue = (value: number): number => Math.round(value);

  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center h-[350px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری تحلیل حفظ مشتریان ..."
          />
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage
          message="خطا در دریافت روند و رشد مشتریان"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!trendsData || trendsData.length === 0) {
    return (
      <div className="card-base card-padding">
        <EmptyState message="داده‌ای برای روند مشتریان یافت نشد" />
      </div>
    );
  }

  const chartData = {
    labels: trendsData.map(item => item.month),
    datasets: [
      {
        type: 'bar' as const,
        label: 'مشتریان قدیمی',
        data: trendsData.map(item => roundDisplayValue(getAdjustedLastMonthValue(item.oldCustomers, item.month))),
        backgroundColor: getChartBackgroundColor(0, currentTheme, 0.8),
        borderColor: getChartColors(currentTheme)[0],
        borderWidth: 2,
        borderRadius: 6,
        order: 2
      },
      {
        type: 'bar' as const,
        label: 'مشتریان جدید',
        data: trendsData.map(item => roundDisplayValue(getAdjustedLastMonthValue(item.newCustomers, item.month))),
        backgroundColor: getChartBackgroundColor(1, currentTheme, 0.8),
        borderColor: getChartColors(currentTheme)[1],
        borderWidth: 2,
        borderRadius: 6,
        order: 2
      },
      {
        type: 'bar' as const,
        label: 'مشتریان غیر فعال',
        data: trendsData.map(item => item.customerLostThismonth),
        backgroundColor: getChartBackgroundColor(2, currentTheme, 0.8),
        borderColor: getChartColors(currentTheme)[2],
        borderWidth: 2,
        borderRadius: 6,
        order: 2
      },
      {
        type: 'line' as const,
        label: 'رشد مشتریان',
        data: trendsData.map(item => roundDisplayValue(getAdjustedLastMonthValue(item.newCustomers, item.month) + getAdjustedLastMonthValue(item.oldCustomers, item.month) - item.customerLostThismonth)),
        borderColor: getChartColors(currentTheme)[3],
        backgroundColor: getChartBackgroundColor(3, currentTheme, 0.2),
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: getChartColors(currentTheme)[3],
        tension: 0.35,
        fill: false,
        order: 1
      }
    ]
  };

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="chart"
          dashboardSource="customerAnalytics"
          onInfo={() => openGuide(CHART_GUIDES.customerTrendsWithBalanceChart)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'customerAnalytics',
            componentType: 'chart',
            description: 'روند مشتریان و تراز ماهانه: تحلیل تعداد مشتریان جدید، قدیمی و غیر فعال در یک سال گذشته  همراه با رشد مشتریان (مشتری جدید + مشتری قدیمی - مشتری غیر فعال در یک سال گذشته )',
            props: { branchId, months, compact },
            gridLayout: {
              containerClass: 'col-span-1'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
        />
      </div>
      <div className="mb-6">
        <h3 className="title-card">{title}</h3>
        {description && (
          <p className="text-text-secondary text-sm mt-1 text-center">
            {description}
          </p>
        )}
      </div>
      <div style={{ height: 350, width: '100%', padding: '20px 0' }}>
        <Chart
          type="bar"
          data={chartData}
          options={{
            ...getChartJsOptions(currentTheme),
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index' as const,
              intersect: false
            },
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  label: function(context: any) {
                    const label = context.dataset.label || '';
                    const value = formatNumber(roundDisplayValue(context.parsed.y));
                    const separator = '\u00A0:\u00A0';
                    return `${label}${separator}${value}`;
                  }
                }
              }
            },
            scales: {
              ...getChartJsOptions(currentTheme).scales,
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.x?.ticks,
                  autoSkip: false,
                  maxRotation: 45,
                  minRotation: 45
                }
              },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y
              }
            }
          }}
        />
      </div>

      {/* Data Summary (last month) */}
      {lastTrend && (
        <div className="mt-6 p-4 bg-bg-secondary rounded-lg">
          <h4 className="text-sm font-medium text-text-primary mb-3">خلاصه داده‌ها (آخرین ماه)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-text-tertiary mb-1">مشتریان جدید</div>
              <div className="text-lg font-semibold text-text-primary">
                {formatNumber(roundDisplayValue(lastTrendAdjustedNew || 0))} نفر
              </div>
            </div>
            <div className="text-center">
              <div className="text-text-tertiary mb-1">مشتریان قدیمی</div>
              <div className="text-lg font-semibold text-text-primary">
                {formatNumber(roundDisplayValue(lastTrendAdjustedOld || 0))} نفر
              </div>
            </div>
            <div className="text-center">
              <div className="text-text-tertiary mb-1">مشتریان غیر فعال</div>
              <div className="text-lg font-semibold text-status-error">
                {formatNumber(lastTrend.customerLostThismonth || 0)} نفر
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTrendsWithBalanceChart;
